import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import FadeIn, { SectionHeading } from "./FadeIn";
import AnimatedNumber from "./motion/AnimatedNumber";
import { Slider } from "@/components/ui/slider";
import { monthlyPayment, fmt } from "./Hero";
import { useInViewMount } from "./three/shared";

const ComparisonObject = lazy(() => import("./three/ComparisonObject"));

const SCENARIOS = [
  {
    key: "fixed",
    name: "Fixed Rate",
    obj: "fixed",
    term: "5-Year Term",
    rate: 4.89,
    amort: "25 Years",
    flexibility: "Moderate",
    note: "Predictable payments. Rate locked for the full term.",
  },
  {
    key: "variable",
    name: "Variable Rate",
    obj: "variable",
    term: "5-Year Term",
    rate: 5.15,
    amort: "25 Years",
    flexibility: "High",
    note: "Moves with prime. Convertible to fixed at any time.",
  },
  {
    key: "short",
    name: "Short Term",
    obj: "short",
    term: "2-Year Term",
    rate: 5.49,
    amort: "25 Years",
    flexibility: "High",
    note: "Short commitment. Re-position sooner if rates fall.",
  },
  {
    key: "long",
    name: "Long Term",
    obj: "long",
    term: "10-Year Term",
    rate: 5.34,
    amort: "25 Years",
    flexibility: "Lower",
    note: "A decade of payment certainty for long holds.",
  },
];

export default function Comparison() {
  const [amount, setAmount] = useState(650000);
  const [active, setActive] = useState("fixed");
  const [render3D, setRender3D] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mobile = window.matchMedia("(max-width: 767px)").matches;
    setRender3D(!reduced && !mobile);
  }, []);

  const activeScenario = SCENARIOS.find((s) => s.key === active);
  const stageRef = useRef(null);
  const inView = useInViewMount(stageRef);
  const payments = SCENARIOS.map((s) => ({ key: s.key, name: s.name, pay: monthlyPayment(amount, s.rate, 25) }));
  const maxPay = Math.max(...payments.map((p) => p.pay), 1);
  const activePayment = monthlyPayment(amount, activeScenario.rate, 25);

  return (
    <section id="compare" data-testid="comparison-section" className="py-24 lg:py-36 bg-charcoal-800/95">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          align="center"
          label="Compare Scenarios"
          title="One Mortgage. Many Strategies."
          sub="See how rate type and term change your payment on the same mortgage amount."
        />

        <FadeIn className="max-w-xl mx-auto mt-14">
          <div className="flex items-baseline justify-between mb-3">
            <label className="text-[11px] uppercase tracking-[0.2em] text-ivory/50">Mortgage Amount</label>
            <span data-testid="compare-amount-value" className="font-serif text-2xl text-champagne">${fmt(amount)}</span>
          </div>
          <Slider
            data-testid="compare-amount-slider"
            value={[amount]}
            min={200000}
            max={2000000}
            step={10000}
            onValueChange={([v]) => setAmount(v)}
            className="[&_[role=slider]]:bg-gold [&_[role=slider]]:border-champagne [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5"
          />
        </FadeIn>

        <FadeIn className="max-w-5xl mx-auto mt-12">
          <div data-testid="compare-stage" className="glass-panel rounded-md p-7 lg:p-9 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-4">
              <p className="gold-label mb-4" data-testid="compare-stage-name">{activeScenario.name}</p>
              <div className="flex items-end gap-3 flex-wrap">
                <AnimatedNumber
                  value={activeScenario.rate}
                  prefix=""
                  suffix="%"
                  decimals={2}
                  className="font-serif text-5xl gold-text-gradient"
                />
                <span className="mb-2 px-2.5 py-1 text-[9px] tracking-[0.2em] text-bronze border border-bronze/40 rounded-sm" data-testid="compare-stage-rate-tag">
                  EXAMPLE RATE
                </span>
              </div>
              <p className="mt-5 text-sm text-ivory/50 font-light">
                Est. Payment{" "}
                <span data-testid="compare-stage-payment">
                  <AnimatedNumber value={activePayment} suffix="/mo" className="text-ivory font-medium text-base" />
                </span>
              </p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between max-w-[220px]">
                  <span className="text-ivory/45 font-light">Term</span>
                  <span className="text-ivory/80">{activeScenario.term}</span>
                </div>
                <div className="flex justify-between max-w-[220px]">
                  <span className="text-ivory/45 font-light">Flexibility</span>
                  <span className="text-gold">{activeScenario.flexibility}</span>
                </div>
              </div>
            </div>

            <div className="md:col-span-4" data-testid="compare-chart">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ivory/45 mb-3 text-center">Payment Comparison</p>
              <div className="flex items-end gap-3 h-32">
                {payments.map((p) => (
                  <div key={p.key} className="flex-1 h-full flex flex-col justify-end items-center gap-2">
                    <motion.div
                      className={`w-full rounded-t-sm ${p.key === active ? "bg-gold" : "bg-bronze/40"}`}
                      initial={false}
                      animate={{ height: `${(p.pay / maxPay) * 100}%` }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                    />
                    <span className={`text-[9px] tracking-[0.12em] uppercase ${p.key === active ? "text-gold" : "text-ivory/40"}`}>
                      {p.name.split(" ")[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div ref={stageRef} className="hidden md:block md:col-span-4 h-44">
              {render3D && inView && (
                <Suspense fallback={null}>
                  <ComparisonObject type={activeScenario.obj} />
                </Suspense>
              )}
            </div>
          </div>
        </FadeIn>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SCENARIOS.map((s, i) => {
            const payment = monthlyPayment(amount, s.rate, 25);
            const isActive = active === s.key;
            return (
              <FadeIn key={s.key} delay={i * 0.08} variant="depth">
                <button
                  data-testid={`compare-card-${s.key}`}
                  onClick={() => setActive(s.key)}
                  className={`w-full text-left glass-panel depth-card rounded-md p-7 h-full ${
                    isActive
                      ? "border-gold/70 bg-gold/[0.06] lg:-translate-y-2.5 shadow-[0_26px_60px_rgba(0,0,0,0.55),0_0_40px_rgba(229,192,123,0.08)]"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-serif text-xl text-ivory">{s.name}</h3>
                    <span className={`w-2 h-2 rounded-full transition-colors duration-500 ${isActive ? "bg-gold" : "bg-white/15"}`} />
                  </div>
                  <p className="font-serif text-3xl gold-text-gradient mb-1">{s.rate.toFixed(2)}%</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-bronze/80 mb-6">Example Rate</p>
                  <div className="space-y-3 text-sm border-t border-white/10 pt-5">
                    <div className="flex justify-between">
                      <span className="text-ivory/45 font-light">Est. Payment</span>
                      <span data-testid={`compare-payment-${s.key}`}>
                        <AnimatedNumber value={payment} suffix="/mo" className="text-ivory font-medium" />
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ivory/45 font-light">Term</span>
                      <span className="text-ivory/80">{s.term}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ivory/45 font-light">Amortization</span>
                      <span className="text-ivory/80">{s.amort}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ivory/45 font-light">Flexibility</span>
                      <span className="text-gold">{s.flexibility}</span>
                    </div>
                  </div>
                  <p className="mt-5 text-xs text-ivory/40 font-light leading-relaxed">{s.note}</p>
                </button>
              </FadeIn>
            );
          })}
        </div>
        <p className="mt-8 text-center text-[11px] text-ivory/35">
          All rates displayed are example rates for illustration only and are not connected to a live rate source. Payments are estimates — not an offer, approval or commitment to lend. O.A.C.
        </p>
      </div>
    </section>
  );
}
